import { useCallback, useEffect, useRef, useState } from 'react';
import { decodeJwtResponse } from '../../utils';
import { emitLoginGAEvent, emitLogoutGAEvent } from '../../utils/ga-event-helpers';

interface GoogleOneTapOptions {
	client_id: string;
	auto_select?: boolean;
	cancel_on_tap_outside?: boolean;
	context?: string;
	[key: string]: any;
}

interface GoogleUser {
	email: string;
	name?: string;
	picture?: string;
	given_name?: string;
	family_name?: string;
	email_verified?: boolean;
	sub?: string; // Google user ID
}

interface AuthState {
	isAuthenticated: boolean;
	user: GoogleUser | null;
	lastLoginTime?: number;
}

declare global {
	interface Window {
		google: any;
		// @ts-ignore-next-line
		dataLayer?: any[];
	}
}

// Storage key for storing auth state
const AUTH_STORAGE_KEY = 'google_auth';
// Session duration in milliseconds (24 hours)
const SESSION_DURATION = 24 * 60 * 60 * 1000;

/** localStorage getter can throw SecurityError when storage is blocked (ITP, WebView, strict privacy). */
const getLocalStorage = (): Storage | null => {
	if (typeof window === 'undefined') {
		return null;
	}

	try {
		return window.localStorage;
	} catch {
		return null;
	}
};

const googleOneTap = (
	options: GoogleOneTapOptions,
	callback: (response: any) => void,
	isAuthenticated: boolean
) => {
	if (!options.client_id) {
		throw new Error('client_id is required');
	}

	// Don't show One Tap if user is already authenticated
	if (isAuthenticated) {
		console.log('User already authenticated, skipping Google One Tap');
		return;
	}

	if (typeof window !== 'undefined' && window.document) {
		const contextValue = ['signin', 'signup', 'use'].includes(options.context || '')
			? options.context
			: 'signin';

		// Check if the script is already added
		if (!document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
			const googleScript = document.createElement('script');
			googleScript.src = 'https://accounts.google.com/gsi/client';
			googleScript.async = true;
			googleScript.defer = true;
			document.head.appendChild(googleScript);

			googleScript.onload = () => {
				if (window?.google?.accounts?.id) {
					window.google.accounts.id.initialize({
						...options,
						context: contextValue,
						callback,
					});

					// Only prompt if not authenticated
					if (!isAuthenticated) {
						window.google.accounts.id.prompt((notification: any) => {
							console.log('Google One Tap notification:', notification);

							// Handle different notification moments
							if (notification.isNotDisplayed()) {
								console.log('One Tap not displayed:', notification.getNotDisplayedReason());

								// Common reasons:
								// - 'suppressed_by_user': User previously closed the prompt
								// - 'unregistered_origin': Origin not registered in Google Console
								// - 'invalid_client': Invalid client ID
								// - 'browser_not_supported': Browser doesn't support One Tap
								// - 'user_cancel': User cancelled the flow
							}

							if (notification.isSkippedMoment()) {
								console.log('One Tap skipped:', notification.getSkippedReason());

								// Common reasons:
								// - 'auto_cancel': Prompt was automatically cancelled
								// - 'user_cancel': User manually cancelled
								// - 'tap_outside': User clicked outside the prompt
								// - 'issuing_failed': Failed to issue credential
							}

							if (notification.isDismissedMoment()) {
								console.log('One Tap dismissed:', notification.getDismissedReason());

								// Common reasons:
								// - 'credential_returned': Successfully signed in
								// - 'cancel': Cancelled by user
								// - 'manual_cancel': Manually cancelled
							}
						});
					}
				}
			};
		} else {
			console.log('inititalizing...')
			// Script already loaded, just initialize and prompt
			if (window?.google?.accounts?.id && !isAuthenticated) {
				window.google.accounts.id.initialize({
					...options,
					context: contextValue,
					callback,
				});
				window.google.accounts.id.prompt();
			}
		}
	}
};

const useGoogleSignin = (initialize?: boolean) => {
	const [authState, setAuthState] = useState<AuthState | null>(null);
	const [showCheckbox, setShowCheckbox] = useState(false);
	const [isLoading, setIsLoading] = useState(true);

	// Load auth state from localStorage
	const loadAuthState = useCallback((): AuthState | null => {
		const storage = getLocalStorage();
		if (!storage) {
			return null;
		}

		try {
			const storedAuth = storage.getItem(AUTH_STORAGE_KEY);
			if (storedAuth) {
				return JSON.parse(storedAuth);
			}
		} catch (e) {
			console.error('Failed to load auth state:', e);
			try {
				storage.removeItem(AUTH_STORAGE_KEY);
			} catch {}
		}

		return null;
	}, []);

	// Save auth state to localStorage
	const saveAuthState = useCallback((state: AuthState) => {
		const storage = getLocalStorage();
		if (!storage) {
			return;
		}

		try {
			storage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state));
			setAuthState(state);
		} catch (e) {
			console.error('Failed to save auth state:', e);
		}
	}, []);

	// Remove auth state from localStorage
	const removeAuthState = useCallback(() => {
		const storage = getLocalStorage();
		if (!storage) {
			return;
		}

		try {
			storage.removeItem(AUTH_STORAGE_KEY);
			setAuthState(null);
		} catch (e) {
			console.error('Failed to remove auth state:', e);
		}
	}, []);

	// Check if user is authenticated and session is still valid
	const isAuthenticated = useCallback(() => {
		if (!authState?.isAuthenticated || !authState?.user) {
			return false;
		}

		// Check if session has expired
		if (authState.lastLoginTime) {
			const now = Date.now();
			const sessionAge = now - authState.lastLoginTime;

			if (sessionAge > SESSION_DURATION) {
				console.log('Session expired, requiring re-authentication');
				return false;
			}
		}

		return true;
	}, [authState]);

	// One login per distinct authenticated subject, so a silent auto_select re-auth
	// does not re-emit (mirrors the D3 transition guard) (A5).
	const lastEmittedSubRef = useRef<string | null>(null);

	// Handle successful Google sign-in
	const handleCredentialResponse = useCallback((response: any) => {
		try {
			console.log('Google One Tap response received');

			// Decode the JWT token from Google
			const userData = decodeJwtResponse(response.credential);

			console.log('Decoded user data:', {
				email: userData.email,
				name: userData.name,
				email_verified: userData.email_verified
			});

			// Create user object
			const googleUser: GoogleUser = {
				email: userData.email,
				name: userData.name,
				picture: userData.picture,
				given_name: userData.given_name,
				family_name: userData.family_name,
				email_verified: userData.email_verified,
				sub: userData.sub,
			};

			// Update auth state
			const newAuthState: AuthState = {
				isAuthenticated: true,
				user: googleUser,
				lastLoginTime: Date.now(),
			};

			// Save to localStorage only
			saveAuthState(newAuthState);

			// Save auth history for future reference
			const storage = getLocalStorage();
			if (storage) {
				try {
					storage.setItem('google_auth_history', 'true');
				} catch (e) {
					console.error('Failed to save auth history:', e);
				}
			}

			// Log analytics event — once per distinct subject so a silent auto_select
			// re-auth does not double-count the login (A5).
			if (lastEmittedSubRef.current !== userData.sub) {
				lastEmittedSubRef.current = userData.sub;
				emitLoginGAEvent('Google One Tap', userData.sub);
			}

			console.log('User successfully signed in:', googleUser.email);
		} catch (error) {
			console.error('Error handling Google sign-in:', error);
		}
	}, [saveAuthState]);

	// Sign out function
	const signOut = useCallback(() => {
		console.log('Signing out user');

		// Clear localStorage
		removeAuthState();

		// Revoke Google One Tap
		if (window?.google?.accounts?.id) {
			window.google.accounts.id.disableAutoSelect();
		}

		// Log analytics event
		emitLogoutGAEvent('Google One Tap');
		// Allow a genuine re-login after sign-out to emit again (A5).
		lastEmittedSubRef.current = null;
	}, [removeAuthState]);

	// Initialize auth state from localStorage
	useEffect(() => {
		const initAuthState = () => {
			setIsLoading(true);

			const storedAuth = loadAuthState();

			if (storedAuth) {
				// Validate session is still valid
				if (storedAuth.lastLoginTime) {
					const sessionAge = Date.now() - storedAuth.lastLoginTime;

					if (sessionAge <= SESSION_DURATION) {
						console.log('Restoring auth state from localStorage');
						setAuthState(storedAuth);
					} else {
						console.log('Stored session expired, clearing auth state');
						removeAuthState();
					}
				}
			}

			setIsLoading(false);
		};

		initAuthState();
	}, []);

	// Initialize Google One Tap
	useEffect(() => {
		if (!initialize || isLoading) {
			return;
		}

		const authenticated = isAuthenticated();

		if (authenticated) {
			console.log('User already authenticated as:', authState?.user?.email);
			return;
		}

		// Delay showing checkbox
		const checkboxTimer = setTimeout(() => {
			setShowCheckbox(true);
		}, 600);

		// Initialize Google One Tap with options
		const signinOptions: GoogleOneTapOptions = {
			client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID as string,
			auto_select: true, // Auto-select if user has previously signed in
			cancel_on_tap_outside: false,
			context: 'signin',
			ux_mode: 'popup', // Use popup mode
			itp_support: true, // Support for Intelligent Tracking Prevention
		};

		// Small delay to ensure DOM is ready
		const initTimer = setTimeout(() => {
			googleOneTap(signinOptions, handleCredentialResponse, authenticated);
		}, 100);

		return () => {
			clearTimeout(checkboxTimer);
			clearTimeout(initTimer);
		};
	}, [isLoading, isAuthenticated, authState, handleCredentialResponse, initialize]);

	// Expose auth state and methods to parent components
	useEffect(() => {
		if (typeof window !== 'undefined') {
			(window as any).__googleAuth = {
				isAuthenticated: isAuthenticated(),
				user: authState?.user,
				signOut,
			};
		}
	}, [authState, isAuthenticated, signOut]);

	// Log current auth state for debugging
	useEffect(() => {
		if (authState) {
			console.log('Current auth state:', {
				isAuthenticated: authState.isAuthenticated,
				user: authState.user?.email,
				sessionAge: authState.lastLoginTime
					? `${Math.floor((Date.now() - authState.lastLoginTime) / 1000 / 60)} minutes`
					: 'unknown'
			});
		}
	}, [authState]);

	return initialize ? authState : null;
};

export default useGoogleSignin;

// Export utility functions for use in other components
export const checkGoogleAuth = (): boolean => {
	if (typeof window !== 'undefined' && (window as any).__googleAuth) {
		return (window as any).__googleAuth.isAuthenticated;
	}
	return false;
};

export const getGoogleUser = (): GoogleUser | null => {
	if (typeof window !== 'undefined' && (window as any).__googleAuth) {
		return (window as any).__googleAuth.user;
	}
	return null;
};

export const googleSignOut = (): void => {
	if (typeof window !== 'undefined' && (window as any).__googleAuth) {
		(window as any).__googleAuth.signOut();
	}
};
